import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Icon,
  Theme,
  Typography,
  accordionClasses
} from "@outschool/backpack";
import { EnrollmentMeetingPurchaseQueryQuery } from "@outschool/gql-frontend-generated";
import { faCalendarRange, faCheckCircle, faXLight } from "@outschool/icons";
import { useLocale, useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import React from "react";

import { useFutureMeetings } from "../EnrollmentMeetingPurchaseDuration";
import { EnrollmentOrgTeacher } from "../types";

type ScheduledSessionsDropdownProps = {
  // @ts-ignore TS(2339): Property 'activity' does not exist on type '{ __ty... Remove this comment to see the full error message
  activity: EnrollmentMeetingPurchaseQueryQuery["enrollmentMeetingPurchaseForm"]["activity"];
  startTime: Date;
  endTime: Date;
  enrollmentDuration: number | null;
  orgTeacher?: EnrollmentOrgTeacher;
};

const ScheduledSessionsDropdown = ({
  activity,
  startTime,
  endTime,
  enrollmentDuration,
  orgTeacher
}: ScheduledSessionsDropdownProps) => {
  const { t } = useTranslation(
    "client\\components\\enrolling\\ScheduledSessionsDropdown"
  );
  const locale = useLocale();

  const { duration, futureMeetingsLoading, futureMeetings } = useFutureMeetings(
    {
      activityUid: activity?.uid,
      startTime,
      endTime,
      enrollmentDuration,
      orgTeacherUid: orgTeacher?.uid
    }
  );

  const meetings = futureMeetings
    .map(({ startTime, isAvailable }) => {
      const date = new Date(startTime);
      return {
        dayOfWeek: date.toLocaleString(locale, { weekday: "long" }), // Day of the week
        month: date.toLocaleString(locale, { month: "long" }), // Month
        day: date.getDate(), // Day of the month
        time: date.toLocaleTimeString(locale, {
          hour: "2-digit",
          minute: "2-digit"
        }), // Time in HH:MM AM/PM
        isAvailable
      };
    })
    .slice(0, duration ?? futureMeetings.length);

  const time = meetings[0]?.time;
  let sessionTime = "";
  const timeOfDayLocalized = dayjs(time, "h:mmA").format("LT");

  switch (meetings[0]?.dayOfWeek.toLowerCase()) {
    case "monday":
      sessionTime = t("Mondays @ {{timeOfDayLocalized}}", {
        timeOfDayLocalized
      });
      break;
    case "tuesday":
      sessionTime = t("Tuesdays @ {{timeOfDayLocalized}}", {
        timeOfDayLocalized
      });
      break;
    case "wednesday":
      sessionTime = t("Wednesdays @ {{timeOfDayLocalized}}", {
        timeOfDayLocalized
      });
      break;
    case "thursday":
      sessionTime = t("Thursdays @ {{timeOfDayLocalized}}", {
        timeOfDayLocalized
      });
      break;
    case "friday":
      sessionTime = t("Fridays @ {{timeOfDayLocalized}}", {
        timeOfDayLocalized
      });
      break;
    case "saturday":
      sessionTime = t("Saturdays @ {{timeOfDayLocalized}}", {
        timeOfDayLocalized
      });
      break;
    case "sunday":
      sessionTime = t("Sundays @ {{timeOfDayLocalized}}", {
        timeOfDayLocalized
      });
      break;
  }

  if (futureMeetingsLoading || meetings.length <= 0) {
    return <></>;
  }

  return (
    <Accordion
      sx={{
        backgroundColor: "#F7F7F7",
        borderRadius: "8px",
        padding: 8,
        "::before": {
          opacity: 0
        },
        [`&.${accordionClasses.expanded}`]: {
          margin: 0,
          "::before": {
            opacity: 0
          },
          "& + &": {
            "&::before": {
              display: "unset",
              opacity: 0
            }
          }
        }
      }}
    >
      <AccordionSummary>
        <Icon icon={faCalendarRange} size={"medium"} />
        <Box
          flex
          sx={{
            marginLeft: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            paddingRight: 8
          }}
        >
          <Typography variant="body1">{t("Scheduled Sessions")}</Typography>
          <Typography variant="body1">{sessionTime}</Typography>
        </Box>
      </AccordionSummary>
      <Box sx={{ marginLeft: 40, maxHeight: 200, overflow: "scroll" }}>
        {meetings.map(
          (session: { month: string; day: number; isAvailable: boolean }) => (
            <AccordionDetails
              key={session.month + session.day}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingBottom: 8
              }}
            >
              <Typography
                sx={(theme: Theme) =>
                  !session.isAvailable
                    ? {
                        color: theme.palette.error.main,
                        textDecoration: "line-through",
                        fontSize: 14
                      }
                    : { fontSize: 14 }
                }
              >
                {session.month} {session.day.toString()}
              </Typography>
              <Box flex sx={{ paddingRight: 16 }}>
                {session.isAvailable ? (
                  <Icon icon={faCheckCircle} color={"success"} size={"small"} />
                ) : (
                  <>
                    <Typography
                      sx={(theme: Theme) => ({
                        color: theme.palette.error.main,
                        marginRight: ".5em",
                        fontSize: 14
                      })}
                    >
                      {t("UNAVAILABLE")}
                    </Typography>
                    <Icon icon={faXLight} color={"error"} size={"small"} />
                  </>
                )}
              </Box>
            </AccordionDetails>
          )
        )}
        {duration === null && (
          <AccordionDetails
            sx={{
              alignItems: "left",
              paddingBottom: 8,
              paddingRight: 8,
              fontStyle: "italic"
            }}
          >
            <Typography sx={{ fontSize: 14, textAlign: "center" }}>
              {t("Sessions will be added each week until you cancel")}
            </Typography>
          </AccordionDetails>
        )}
      </Box>
    </Accordion>
  );
};

export default ScheduledSessionsDropdown;
